import { useCallback, useEffect, useRef } from 'react'
import { useRecords } from '@/contexts/record-context'

const REMINDER_FIRST_MS = 4 * 60 * 60 * 1000
const REMINDER_REPEAT_MS = 2 * 60 * 60 * 1000

function isWechat(): boolean {
  return /MicroMessenger/i.test(navigator.userAgent)
}

export function useNotification() {
  const { activeTimer } = useRecords()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const requestPermission = useCallback(async () => {
    if (isWechat()) return false
    if (!('Notification' in window)) return false
    if (Notification.permission === 'granted') return true
    if (Notification.permission === 'denied') return false
    const result = await Notification.requestPermission()
    return result === 'granted'
  }, [])

  const sendNotification = useCallback(async (title: string, body: string) => {
    if (isWechat()) return
    if (Notification.permission !== 'granted') return
    try {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        await navigator.serviceWorker.ready
        const registration = await navigator.serviceWorker.getRegistration()
        if (registration) {
          registration.showNotification(title, {
            body,
            icon: '/vite.svg',
            badge: '/vite.svg',
            tag: 'timer-reminder',
            requireInteraction: true,
          })
          return
        }
      }
      new Notification(title, { body, icon: '/vite.svg' })
    } catch (e) {
      console.error('Failed to show notification:', e)
    }
  }, [])

  useEffect(() => {
    if (!activeTimer) {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      return
    }

    const labels: Record<string, string> = { sleep: '睡觉', exercise: '运动', reading: '读书', work: '工作', drunk: '微醺', meditation: '冥想' }
    const fire = () => {
      sendNotification(
        '计时提醒',
        `您的「${labels[activeTimer.category] || activeTimer.category}」计时已超过4小时，别忘了结束记录哦！`
      )
      timerRef.current = setTimeout(fire, REMINDER_REPEAT_MS)
    }

    const elapsed = Date.now() - activeTimer.startTime
    if (elapsed < REMINDER_FIRST_MS) {
      timerRef.current = setTimeout(fire, REMINDER_FIRST_MS - elapsed)
    } else {
      const sinceFirst = elapsed - REMINDER_FIRST_MS
      const nextRepeat = REMINDER_REPEAT_MS - (sinceFirst % REMINDER_REPEAT_MS)
      timerRef.current = setTimeout(fire, nextRepeat)
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [activeTimer, sendNotification])

  return { requestPermission, sendNotification, isWechat: isWechat() }
}
